export const CONSENT_STORAGE_KEY = 'phantompath_cookie_consent_v2';
export const OPTIONAL_TRACKING_READY_EVENT = 'phantompath:optional-tracking-ready';
export const GOOGLE_TRACKING_READY_EVENT = 'phantompath:google-tracking-ready';
export const CONSENT_SETTINGS_EVENT = 'phantompath:open-cookie-settings';

const PRIVATE_ANALYTICS_PATHS = new Set(['/portal']);
const HIDDEN_CONSENT_UI_PATHS = new Set(['/portal', '/success']);
const LOCAL_TRACKING_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);
const GOOGLE_MEASUREMENT_ID = 'G-GPV4BN9LRW';
const META_PIXEL_ID = '959502413778871';
const TIKTOK_PIXEL_ID = 'D9UQGJJC77U7HHC735F0';

const isPortalHostname = () => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname.toLowerCase() === 'portal.phantompathvpn.com';
};

export const isLocalTrackingHost = () => {
  if (typeof window === 'undefined') return false;
  return LOCAL_TRACKING_HOSTNAMES.has(window.location.hostname.toLowerCase());
};

const grantedConsent = {
  ad_storage: 'granted',
  analytics_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
};

const deniedConsent = {
  ad_storage: 'denied',
  analytics_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
};

export const isPrivateAnalyticsPath = (pathname) => {
  const currentPathname = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  return isPortalHostname() || PRIVATE_ANALYTICS_PATHS.has(currentPathname);
};

export const isConsentUiHiddenPath = (pathname) => {
  const currentPathname = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  return isPortalHostname() || HIDDEN_CONSENT_UI_PATHS.has(currentPathname);
};

export const getStoredConsent = () => {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === 'accepted' || value === 'rejected' ? value : null;
  } catch (_error) {
    return null;
  }
};

export const storeConsent = (choice) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch (_error) {}
};

export const hasOptionalConsent = () => getStoredConsent() === 'accepted'
  && !isPrivateAnalyticsPath()
  && !isLocalTrackingHost();

export const ensureGoogleTag = () => {
  if (isPrivateAnalyticsPath() || isLocalTrackingHost()) return;

  if (!window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag('consent', 'default', {
      ...deniedConsent,
      functionality_storage: 'granted',
      security_storage: 'granted',
      wait_for_update: 500,
    });
    if (getStoredConsent() === 'accepted') window.gtag('consent', 'update', grantedConsent);
    window.gtag('js', new Date());
    window.gtag('config', GOOGLE_MEASUREMENT_ID, { send_page_view: false });
  }

  if (!document.querySelector(`script[src*="${GOOGLE_MEASUREMENT_ID}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  window.dispatchEvent(new Event(GOOGLE_TRACKING_READY_EVENT));
};

const loadMetaPixel = () => {
  if (window.fbq) return;
  ((f, b, e, v, n, t, s) => {
    if (f.fbq) return;
    n = f.fbq = function fbq() {
      if (n.callMethod) n.callMethod.apply(n, arguments);
      else n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  window.fbq('init', META_PIXEL_ID);
};

const loadTikTokPixel = () => {
  if (window.ttq) return;
  ((w, d, t) => {
    w.TiktokAnalyticsObject = t;
    const ttq = w[t] = w[t] || [];
    ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie', 'holdConsent', 'revokeConsent', 'grantConsent'];
    ttq.setAndDefer = (target, method) => { target[method] = (...args) => target.push([method].concat(args)); };
    for (let index = 0; index < ttq.methods.length; index += 1) ttq.setAndDefer(ttq, ttq.methods[index]);
    ttq.instance = (pixelId) => {
      const instance = ttq._i[pixelId] || [];
      for (let index = 0; index < ttq.methods.length; index += 1) ttq.setAndDefer(instance, ttq.methods[index]);
      return instance;
    };
    ttq.load = (pixelId, options) => {
      const source = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {};
      ttq._i[pixelId] = [];
      ttq._i[pixelId]._u = source;
      ttq._t = ttq._t || {};
      ttq._t[pixelId] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[pixelId] = options || {};
      const script = d.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `${source}?sdkid=${pixelId}&lib=${t}`;
      const firstScript = d.getElementsByTagName('script')[0];
      firstScript.parentNode.insertBefore(script, firstScript);
    };
    ttq.load(TIKTOK_PIXEL_ID);
  })(window, document, 'ttq');
};

export const enableOptionalTracking = () => {
  if (isPrivateAnalyticsPath() || isLocalTrackingHost()) return;
  ensureGoogleTag();
  window.gtag?.('consent', 'update', grantedConsent);
  loadMetaPixel();
  window.fbq?.('consent', 'grant');
  loadTikTokPixel();
  window.ttq?.grantConsent?.();
  window.dispatchEvent(new Event(OPTIONAL_TRACKING_READY_EVENT));
};

export const disableOptionalTracking = () => {
  window.gtag?.('consent', 'update', deniedConsent);
  window.fbq?.('consent', 'revoke');
  window.ttq?.revokeConsent?.();
};

export const openConsentSettings = () => window.dispatchEvent(new Event(CONSENT_SETTINGS_EVENT));
